<template>
  <div
    ref="panzoomEl"
    class="panzoom-element"
    :class="{ 'is-active': isActive }"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
import panzoom, { type PanZoom } from "panzoom";

const props = defineProps<{
  zoomLevel: number;
  isActive: boolean;
}>();

const emit = defineEmits(["update:zoomLevel"]);
const zoomLevel = useVModel(props, "zoomLevel", emit);

// The panzoom Library
const panzoomEl = ref<HTMLElement | null>(null);
let panzoomInstance: PanZoom | null = null;

const initPanzoom = () => {
  if (!panzoomEl.value) return;

  if (panzoomInstance) {
    return;
  }

  panzoomInstance = panzoom(panzoomEl.value, {
    minZoom: 1,
    maxZoom: 3,
    zoomDoubleClickSpeed: props.isActive ? 20 : 0,
    bounds: true,
    boundsPadding: 1,

    onTouch: (e) => {
      // If we are not active, do not call preventDefault
      if (!props.isActive) return false;

      // If we are using two fingers, call preventDefault
      if (e.touches.length === 2) return true;

      // If we are using one finger and not zoomed in, do not call preventDefault
      if (e.touches.length === 1 && zoomLevel.value === 1) {
        return false;
      }

      // Add the 'allow-touch' class to the nested elements to allow touch events to bubble through
      const target = e.target as HTMLElement;
      if (
        target.classList.contains("allow-touch") ||
        target.parentElement?.classList.contains("allow-touch")
      ) {
        return false;
      }

      return true;
    },
    beforeWheel: (e) => {
      if (!props.isActive) return true;

      // allow wheel-zoom only if ALT or CMD is also pressed. Otherwise - ignore
      return !(e.altKey || e.metaKey);
    },
  });

  panzoomInstance.on("zoom", () => {
    const scale = panzoomInstance?.getTransform().scale;
    if (scale) zoomLevel.value = scale;
  });
};

// Zoom to the given zoom level
const zoomTo = (zoomLevel: number) => {
  if (!panzoomInstance || panzoomInstance.isPaused()) return;
  const centerX = (panzoomEl.value?.clientWidth ?? 1) / 2;
  const centerY = (panzoomEl.value?.clientHeight ?? 1) / 2;

  const currentZoom = panzoomInstance.getTransform().scale;

  // Prevent NaN values
  if (Number.isNaN(centerX) || Number.isNaN(centerY) || Number.isNaN(zoomLevel / currentZoom))
    return;

  panzoomInstance.zoomTo(centerX, centerY, zoomLevel / currentZoom); // It needs the transform ratio, not the absolute zoom value);
};

watch(
  () => process.client && !!panzoomEl.value,
  (isReady) => {
    if (isReady) {
      initPanzoom();
    }
  },
  {
    immediate: true,
  }
);

// Watch the zoom prop to update the panzoom zoomlevel
watch(
  () => props.zoomLevel,
  (zoom) => {
    zoomTo(zoom);
  },
  { immediate: true }
);

// Watch the isActive prop to pause/unpause the panzoom functionality
watch(
  () => props.isActive,
  () => {
    if (props.isActive) {
      panzoomInstance?.resume();
    } else {
      panzoomInstance?.pause();
    }
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
.panzoom-element {
  width: 100%;
  height: 100%;

  &:not(.is-active) {
    z-index: 1;
  }
  &.is-active {
    z-index: 2;
  }
}
</style>
